import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import ExperienceCard from '../components/experience/ExperienceCard';
import ExperienceFilter from '../components/experience/ExperienceFilter';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Experience = () => {
  const [experiences, setExperiences] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = ['all', 'frontend', 'backend', 'blockchain', 'embedded', 'devops', 'ai'];

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const response = await apiService.getExperiences();
        setExperiences(response.data || []);
        setFiltered(response.data || []);
      } catch (error) {
        console.error('Error fetching experiences:', error);
        // Fallback data
        const fallbackData = [
          {
            id: 1,
            company: 'Self-Employed',
            role: 'Full Stack Software Engineer',
            start_date: '2021-01-01',
            is_current: true,
            description: 'Design, develop, and deploy modern software solutions for clients, academic initiatives, and personal ventures.',
            technologies: ['React', 'Node.js', 'PHP', 'Laravel', 'MySQL', 'MongoDB', 'Solidity'],
            achievements: [
              'Developed 15+ full-stack solutions across healthcare, education, fintech, and public-safety domains',
              'Built decentralized application prototypes integrating blockchain technology and smart contracts'
            ],
            category: 'fullstack'
          },
          {
            id: 2,
            company: 'Federal University Dutse',
            role: 'Software Engineering Student',
            start_date: '2023-09-01',
            is_current: true,
            description: 'Pursuing B.Sc. in Software Engineering with coursework in Data Structures, Database Systems, Operating Systems, and more.',
            technologies: ['C++', 'Java', 'Python', 'SQL', 'Algorithms'],
            achievements: [
              'Maintaining 3.5+ GPA',
              'Leading multiple academic projects'
            ],
            category: 'academic'
          }
        ];
        setExperiences(fallbackData);
        setFiltered(fallbackData);
      } finally {
        setLoading(false);
      }
    };
    fetchExperiences();
  }, []);

  const handleFilter = (category) => {
    setActiveFilter(category);
    if (category === 'all') {
      setFiltered(experiences);
    } else {
      setFiltered(experiences.filter(exp => exp.category === category));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Experience
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            My <span className="gradient-text">Journey</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            A timeline of my professional experience and academic journey
          </p>
        </motion.div>

        {/* Filters */}
        <ExperienceFilter 
          categories={categories}
          activeFilter={activeFilter}
          onFilter={handleFilter}
        />

        {/* Experience Cards */}
        <div className="space-y-6">
          {filtered.map((experience, index) => (
            <ExperienceCard 
              key={experience.id} 
              experience={experience}
              index={index}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No experiences found for this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Experience;